import { useState, type ReactNode } from "react";
import { Check, X } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Section } from "./ui/section";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import {
  useApproveJoinRequest,
  useDenyJoinRequest,
  useJoinRequests,
} from "../api/hooks";
import { formatRole } from "../lib/groupRole";
import type { Group, JoinRequest } from "../api/types";

type ClaimableRole = "MEMBER" | "VIEWER";

interface JoinRequestsPanelProps {
  group: Group;
}

export function JoinRequestsPanel({
  group,
}: JoinRequestsPanelProps): ReactNode {
  const requestsQ = useJoinRequests(group.id);
  const deny = useDenyJoinRequest(group.id);
  const [approving, setApproving] = useState<JoinRequest | null>(null);
  const [denyingId, setDenyingId] = useState<string | null>(null);

  async function onDeny(req: JoinRequest): Promise<void> {
    setDenyingId(req.id);
    try {
      await deny.mutateAsync(req.id);
    } finally {
      setDenyingId((cur) => (cur === req.id ? null : cur));
    }
  }

  const requests = requestsQ.data?.items ?? [];
  const participantNames = new Map(
    group.participants.map((p) => [p.id, p.displayName]),
  );

  return (
    <Section title="Join requests" contentClassName="space-y-stack-sm">
      {requests.length === 0 ? (
        <p className="py-6 text-center text-body-md text-on-surface-variant">
          No pending join requests.
        </p>
      ) : (
        requests.map((req) => (
          <div
            key={req.id}
            className="flex items-center gap-3 rounded-md border border-outline-variant/60 p-4"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-body-md font-bold">
                {req.applicantDisplayName}{" "}
                <span className="text-label-sm font-normal text-on-surface-variant">
                  · proposes {formatRole(req.proposedRole)}
                </span>
              </p>
              <p className="text-label-sm text-on-surface-variant">
                {req.mode === "TAKEOVER"
                  ? `wants to take over "${
                      participantNames.get(req.targetParticipantId ?? "") ??
                      "an existing guest"
                    }"`
                  : `wants to join as a new participant${
                      req.requestedDisplayName
                        ? ` (${req.requestedDisplayName})`
                        : ""
                    }`}
              </p>
            </div>
            <Button
              size="icon"
              variant="ghost"
              aria-label="Deny request"
              onClick={() => void onDeny(req)}
              disabled={denyingId === req.id}
            >
              <X className="size-4" />
            </Button>
            <Button
              size="icon"
              variant="default"
              aria-label="Approve request"
              onClick={() => setApproving(req)}
            >
              <Check className="size-4" />
            </Button>
          </div>
        ))
      )}

      <ApproveJoinRequestDialog
        groupId={group.id}
        request={approving}
        onClose={() => setApproving(null)}
      />
    </Section>
  );
}

function ApproveJoinRequestDialog({
  groupId,
  request,
  onClose,
}: {
  groupId: string;
  request: JoinRequest | null;
  onClose: () => void;
}): ReactNode {
  return (
    <Dialog
      open={request !== null}
      onOpenChange={(open) => (!open ? onClose() : null)}
    >
      <DialogContent>
        {request ? (
          <ApproveJoinRequestBody
            groupId={groupId}
            request={request}
            onClose={onClose}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ApproveJoinRequestBody({
  groupId,
  request,
  onClose,
}: {
  groupId: string;
  request: JoinRequest;
  onClose: () => void;
}): ReactNode {
  const approve = useApproveJoinRequest(groupId);
  const [role, setRole] = useState<ClaimableRole>(request.proposedRole);
  const [displayName, setDisplayName] = useState<string>(
    request.requestedDisplayName ?? request.applicantDisplayName,
  );

  async function onSubmit(): Promise<void> {
    const body: { role: ClaimableRole; displayName?: string } = { role };
    if (request.mode === "NEW") body.displayName = displayName;
    try {
      await approve.mutateAsync({ requestId: request.id, body });
      onClose();
    } catch {
      // Surface the failure inline; keep the dialog open so the OWNER can
      // adjust and retry.
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Approve join request</DialogTitle>
        <DialogDescription>
          {request.mode === "TAKEOVER"
            ? `${request.applicantDisplayName} will take over an existing guest. Historical expenses stay attached.`
            : `${request.applicantDisplayName} will join as a new participant.`}
        </DialogDescription>
      </DialogHeader>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
      >
        <div className="space-y-3">
          <Label htmlFor="approve-role">Role</Label>
          <select
            id="approve-role"
            value={role}
            onChange={(e) => setRole(e.target.value as ClaimableRole)}
            className="flex h-11 w-full rounded-sm bg-surface-container-low px-4 text-body-md text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <option value="MEMBER">{formatRole("MEMBER")}</option>
            <option value="VIEWER">{formatRole("VIEWER")}</option>
          </select>
        </div>
        {request.mode === "NEW" ? (
          <div className="space-y-3">
            <Label htmlFor="approve-display-name">Display name</Label>
            <Input
              id="approve-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={64}
              required
            />
          </div>
        ) : null}
        {approve.error ? (
          <p className="text-label-md text-error">
            Couldn't approve this request — it may no longer be pending.
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={approve.isPending}>
            {approve.isPending ? "Approving…" : "Approve"}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}
